import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

interface InstantQuoteResult {
  success: boolean;
  lead: {
    id: string;
    name: string;
    address: string;
  };
  measurement: {
    totalSquares: number;
    source: string;
  };
  pricing: {
    good: number;
    better: number;
    best: number;
  };
  proposal: {
    id: string;
    shareId: string;
    shareUrl: string;
  };
  message: string;
}

export function useInstantQuote() {
  const navigate = useNavigate();
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState<string>("");
  const [result, setResult] = useState<InstantQuoteResult | null>(null);

  const generateInstantQuote = async (
    name: string,
    address: string,
    phone?: string
  ): Promise<InstantQuoteResult | null> => {
    setIsGenerating(true);
    setProgress("Creating lead...");
    setResult(null);

    try {
      setProgress("Fetching roof measurements...");
      
      const { data, error } = await supabase.functions.invoke('instant-quote-generator', {
        body: { name, address, phone },
      });

      if (error) {
        throw new Error(error.message);
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to generate quote');
      }

      setProgress("Quote ready!");
      setResult(data);
      
      toast.success(data.message, {
        action: {
          label: "View Proposal",
          onClick: () => navigate(data.proposal.shareUrl),
        },
      });

      return data;
    } catch (error) {
      console.error("Error generating instant quote:", error);
      toast.error(error instanceof Error ? error.message : "Failed to generate quote");
      return null;
    } finally {
      setIsGenerating(false);
      setProgress("");
    }
  };

  const openProposal = (shareId: string) => {
    navigate(`/proposal/${shareId}`);
  };

  const openProposalInNewTab = (shareId: string) => {
    window.open(`/proposal/${shareId}`, '_blank');
  };

  return {
    generateInstantQuote,
    openProposal,
    openProposalInNewTab,
    isGenerating,
    progress,
    result,
  };
}
